import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  BadgeCheck,
  BedDouble,
  CalendarClock,
  CarFront,
  Compass,
  FileCheck2,
  Headphones,
  HeartHandshake,
  MapPinned,
  Plane,
  Phone,
  ShieldCheck,
  Star,
  Timer,
  TrainFront,
  UtensilsCrossed,
} from "lucide-react";
import { WhatsAppIcon } from "../components/icons";
import {
  BRAND,
  FRAME_COLORS,
  HERO_IMG,
  SERVICES,
  TEASER,
  TRIPS,
  WHY_US,
} from "../data/site";

const ICONS = {
  plane: Plane,
  bed: BedDouble,
  visa: FileCheck2,
  shield: ShieldCheck,
  headphones: Headphones,
  train: TrainFront,
  car: CarFront,
  food: UtensilsCrossed,
  calendar: CalendarClock,
  map: MapPinned,
};

const WHY_ICONS = {
  footprints: { c: "bg-teal/10 text-teal", i: BadgeCheck },
  handshake: { c: "bg-coral/10 text-coral", i: HeartHandshake },
  timer: { c: "bg-navy/10 text-navy", i: Timer },
  headset: { c: "bg-[#25D366]/10 text-[#1da851]", i: Headphones },
  compass: { c: "bg-plum/10 text-plum", i: Compass },
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

const STATS = [
  { value: "2021", label: "Established" },
  { value: "100%", label: "Repeat rate" },
  { value: "24/7", label: "On-trip support" },
  { value: "2–50", label: "Group size handled" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-[92vh] items-center overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Lake Bled, Europe"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-28 pt-36 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-gold sm:text-sm">
            Manvi Travels · Est. 2021
          </p>
          <h1
            className="mt-6 max-w-3xl font-script text-5xl font-bold leading-[1.1] text-white sm:text-7xl"
            style={{ textShadow: "0 2px 28px rgba(0,0,0,0.5)" }}
          >
            Come, see the world with us...
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-white/90 sm:text-lg">
            Completely customised, tailor-made holidays — with unmatched
            expertise in Asia, Africa, the Middle East and Europe.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 font-display text-base font-bold text-navy transition-transform duration-200 hover:scale-[1.03] hover:bg-gold-hover"
            >
              <WhatsAppIcon className="h-5 w-5" />
              Plan My Holiday
            </a>
            <Link
              to="/destinations"
              data-testid="hero-explore-link"
              className="flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 font-display text-base font-bold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/10"
            >
              Explore Destinations
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="relative z-10 mx-auto -mt-14 max-w-6xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {STATS.map((s) => (
            <div
              key={s.label}
              data-testid="home-stat-card"
              className="rounded-2xl border border-black/5 bg-white/80 p-6 text-center shadow-lg backdrop-blur-xl"
            >
              <div className="font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
                {s.value}
              </div>
              <div className="mt-1 text-[10px] font-semibold uppercase tracking-normal text-gray-500 sm:text-xs sm:tracking-[0.15em]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-coral">
              Why Manvi Travels
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Honest advice. First-hand experience.
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-gray-600 sm:text-lg">
              Most places we send you to, we have personally visited. That
              first-hand knowledge is your biggest advantage — no time wasted,
              no tourist traps, and every important place covered at the
              minimum possible cost.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-8 inline-flex items-center gap-2 font-display font-bold text-navy transition-colors duration-200 hover:text-gold-deep"
            >
              Read the full story
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="space-y-4 lg:col-span-7">
            {WHY_US.map((w, i) => {
              const cfg = WHY_ICONS[w.icon];
              const Icon = cfg.i;
              return (
                <motion.div
                  key={w.title}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  data-testid="why-us-card"
                  className="flex gap-5 rounded-2xl border border-black/5 bg-white p-6 transition-shadow duration-200 hover:shadow-xl"
                >
                  <div
                    className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${cfg.c}`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-bold text-navy">
                      {w.title}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-gray-600 sm:text-base">
                      {w.text}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="noise-dark bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold">
            One-Stop Shop
          </p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
            Start to end, everything is handled.
          </h2>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon];
              return (
                <motion.div
                  key={s.label}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.4, delay: (i % 5) * 0.06 }}
                  data-testid="service-card"
                  className="rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm transition-transform duration-200 hover:-translate-y-1"
                >
                  <Icon className="h-7 w-7 text-gold" />
                  <p className="mt-4 text-sm font-semibold leading-snug text-white">
                    {s.label}
                  </p>
                </motion.div>
              );
            })}
          </div>
          <p className="mt-10 text-sm text-white/60 sm:text-base">
            ...and many such services that make your trip effortless. Groups
            from a honeymoon couple of 2 to corporate gatherings of 40–50
            people.
          </p>
        </div>
      </section>

      {/* DESTINATIONS TEASER BENTO */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-deep">
              Where to next
            </p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Destinations we know inside out
            </h2>
          </div>
          <Link
            to="/destinations"
            data-testid="home-destinations-link"
            className="flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-display text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
          >
            View all destinations
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-12">
          {TEASER.map((d, i) => (
            <motion.div
              key={d.name}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.45, delay: i * 0.07 }}
              className={`relative group overflow-hidden rounded-2xl ring-1 ring-black/5 ${d.cls}`}
            >
              <img
                src={d.img}
                alt={d.name}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 p-6">
                <h3 className="font-display text-2xl font-bold text-white">
                  {d.name}
                </h3>
                <p className="mt-1 text-sm text-white/80">{d.blurb}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* GOOGLE RATING BAND */}
      <section className="noise-dark bg-coral py-20">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6">
          <div className="flex items-center justify-center gap-1.5">
            <div className="relative flex">
              <div className="flex gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-8 w-8 text-white/30" />
                ))}
              </div>
              <div
                className="absolute inset-0 flex gap-1.5 overflow-hidden"
                style={{ width: "94%" }}
              >
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    data-testid="google-star"
                    className="h-8 w-8 shrink-0 fill-gold text-gold"
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="mt-5 font-display text-5xl font-extrabold tracking-tighter text-white sm:text-6xl">
            {BRAND.googleRating}
          </div>
          <p className="mt-2 text-sm font-bold uppercase tracking-[0.2em] text-white/85">
            Rated on Google
          </p>
          <p className="mx-auto mt-4 max-w-xl text-base text-white/80 sm:text-lg">
            Happy travellers from Chembur and across Mumbai rate their Manvi
            Travels holidays on Google.
          </p>
          <Link
            to="/our-trips"
            data-testid="home-trips-link"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 font-display font-bold text-coral transition-transform duration-200 hover:scale-[1.03]"
          >
            See our happy clients
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CLIENT PHOTOS STRIP */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-plum">
                Our happy clients
              </p>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Fresh from our clients&apos; travels
              </h2>
            </div>
            <Link
              to="/our-trips"
              data-testid="home-trips-strip-link"
              className="flex items-center gap-2 font-display font-bold text-navy transition-colors duration-200 hover:text-gold-deep"
            >
              Open full gallery
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <div className="no-scrollbar mt-10 flex gap-4 overflow-x-auto px-4 pb-4 sm:px-6 lg:px-8">
          {TRIPS.slice(0, 14).map((t, i) => (
            <div
              key={t.src}
              data-testid="client-photo-thumb"
              className={`w-60 shrink-0 overflow-hidden rounded-2xl border-4 bg-white shadow-md sm:w-72 ${FRAME_COLORS[i % FRAME_COLORS.length]}`}
            >
              <img
                src={t.src}
                alt={t.place}
                loading="lazy"
                className="aspect-[4/5] w-full object-cover"
              />
              <p className="truncate px-3 py-2 text-center font-display text-xs font-bold text-navy sm:text-sm">
                {t.place}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8 lg:pb-32">
        <div className="rounded-3xl bg-gold px-6 py-16 text-center sm:px-12">
          <h2 className="mx-auto max-w-2xl font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
            Tell us your dream destination. We&apos;ll plan it end to end.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-navy/80 sm:text-lg">
            From a honeymoon for two to a corporate trip of fifty — flights,
            hotels, visas, insurance and 24/7 support, all sorted.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
            <a
              href={BRAND.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-final-whatsapp-cta"
              className="flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-display font-bold text-white transition-transform duration-200 hover:scale-[1.03]"
            >
              <WhatsAppIcon className="h-5 w-5" />
              WhatsApp Sagar Now
            </a>
            <a
              href={BRAND.phoneHref}
              data-testid="home-final-phone-cta"
              className="flex items-center gap-2 rounded-full border-2 border-navy/20 px-8 py-4 font-display font-bold text-navy transition-colors duration-200 hover:bg-navy hover:text-white"
            >
              <Phone className="h-5 w-5" />
              {BRAND.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
